import 'moment-timezone';

import * as localForage from 'localforage';

import { Injectable } from '@angular/core';
import { createStore } from '@ngneat/elf';
import {
    deleteAllEntities, getAllEntities, getEntity, upsertEntities, withEntities
} from '@ngneat/elf-entities';
import {
    clearRequestsCache, createRequestsCacheOperator, createRequestsStatusOperator,
    initializeAsPending, updateRequestCache, updateRequestStatus, withRequestsCache,
    withRequestsStatus
} from '@ngneat/elf-requests';
import { DefaultObject } from '@shared/interfaces/index';
import { CacheService } from '@shared/services';
import { persistState } from '@shared/third-party/persist-state/persist-state';

import { BaseRepository } from '../base.repository';
import { STORETIMEOUT } from '../db';

export interface Inventory {
	product_id: number;
	company_id: number;
	name: string;
	pricing_inventory_type_id: string;
	telematics_tracker_id: string;
	is_active: boolean;
	fcusto;
	is_deleted: boolean;
}

const store = createStore(
	{ name: 'inventory' },
	withEntities<Inventory, 'product_id'>({ idKey: 'product_id' }),
	withRequestsStatus<`inventory`>(initializeAsPending('inventory')),
	withRequestsCache<`inventory`>()
);
export const trackInventoryRequestsStatus = createRequestsStatusOperator(store);
export const persist = persistState(store, {
	key: 'inventory',
	storage: localForage,
});
export const skipWhileInventoryCached = createRequestsCacheOperator(store);
setInterval(() => store.update(clearRequestsCache()), STORETIMEOUT);

@Injectable({ providedIn: 'root' })
export class InventoryRepository extends BaseRepository {
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(private cache: CacheService) {
		super();
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - store
	 **-------------------------------------------------------------------------------------
	 */
	get store() {
		return store;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - queries
	 **-------------------------------------------------------------------------------------
	 */
	getAll = () => store.query(getAllEntities());
	get = id => store.query(getEntity(id));
	getAvailableProducts = selectedInventoryTypeId =>
		store
			.query(getAllEntities())
			.filter(
				({ pricing_inventory_type_id }) =>
					pricing_inventory_type_id === selectedInventoryTypeId
			);
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - add
	 **-------------------------------------------------------------------------------------
	 */
	public async add(items) {
		const companyId = await this.cache.get('secure:company_id');
		const inventoryItems = items.filter((e: any) => e.company_id == companyId);
		store.update(deleteAllEntities());
		store.update(
			upsertEntities(inventoryItems),
			updateRequestCache('inventory', { ttl: STORETIMEOUT }),
			updateRequestStatus('inventory', 'success')
		);
	}
}
